import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { differenceInMilliseconds } from 'date-fns';
import { injectTestId } from '@cvent/nucleus-test-automation';
import NotificationAlert from '@components/notifications/NotificationAlert';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@hooks/useTheme';
import {
  ENGAGEMENT_MESSAGE_TYPE,
  ENGAGEMENT_PANEL_IDS,
  DEFAULT_AUTO_HIDE_TIMEOUT
} from '@components/engagement/constants';
import { Z_INDEX } from '@components/shared/zIndex';
import AgendaItemReminderToast from '@components/notifications/AgendaItemReminderToast';
import useEngagementMessages from '@components/engagement/useEngagementMessages';
import { isApptNotificationChannel } from '@components/notifications/utils';
import useMeQuery from '@hooks/useMeQuery';
import useAppFeatures from '@hooks/useAppFeatures';
import useSimulateEngagementMessages from '@hooks/useSimulateEngagementMessages';

const useStyles = () => {
  const { font } = useTheme();
  return {
    linkedContent: {
      marginTop: 8,
      span: {
        color: font.color.interactive.base,
        fontSize: font.base.size.s,
        paddingLeft: 0,
        fontWeight: font.base.weight.bold,
        '&:hover': {
          textDecoration: 'underline'
        }
      }
    }
  };
};

const GAP_BETWEEN_TOASTS = 4;
const TOAST_PADDING_TOP = 4;
const CHAT_BOT_HEIGHT = 64;

/**
 * Component to display stacked toast messages
 */
const EngagementToasts = ({
  topPosition,
  messages = [],
  clearMessage = () => null,
  eventId,
  surveyUrl
}) => {
  const { isMobile, isMobileOrTablet } = useBreakpoints();
  const { linkedContent: linkedContentStyles } = useStyles();
  const { activePanel, agendaItem } = useEngagementMessages();

  /**
   * When a toast for a panel is clicked we clear need to remove it from the stack
   * @param {*} panelId
   */
  const onClick = panelId => {
    clearMessage(panelId);
  };

  // Grab the first 3 messages and stack toasts
  const toasts = messages.slice(0, 3);
  const toastsHeightRef = useRef([0, 0, 0]);
  const didCalculateHeight = useRef();
  const [, applyToastsHeightAndTopPosition] = useState();

  /**
   * The toasts top position is calculated based on the toastsHeightRef which holds the height for each toast
   * at each index. Since this is a ref even if we have updated the ref after each toast is in the document we
   * still need to force a render when the toast length has changed. This takes care of the posibility of toast
   * displaying on top of each other when one is dismissed (https://jira.cvent.com/browse/HUB-36564)
   */
  useEffect(() => {
    // Update needs to happen in a different scope so we give all the toasts time to be on the document
    const timeout = setTimeout(() => {
      applyToastsHeightAndTopPosition({});
    }, 0);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [applyToastsHeightAndTopPosition, toasts.length, agendaItem]);

  return (
    <div
      {...injectTestId('engagement-toasts-container')}
      css={{
        position: 'fixed',
        zIndex:
          isMobileOrTablet && activePanel
            ? Z_INDEX.ENGAGEMENT_TOASTS
            : Z_INDEX.ENGAGEMENT_TOASTS_ACTIVE,
        right: 8,
        padding: 20,
        ...(isMobileOrTablet ? { bottom: 0 } : { top: topPosition })
      }}
    >
      {toasts.map((toast, index) => {
        const {
          panelId,
          publishedAt,
          dismissTimeout = null,
          message: {
            icon,
            body,
            renderBody,
            contentType,
            linkedContent,
            image
          },
          data,
          onDismiss,
          onSettingsClick,
          onAlertClick
        } = toast;

        const currentTime = new Date(Date.now());
        const remainingTime = differenceInMilliseconds(
          currentTime,
          publishedAt
        );
        const autoHideTimeout = dismissTimeout || DEFAULT_AUTO_HIDE_TIMEOUT;
        const toastTimeout = autoHideTimeout - remainingTime;

        if (toastTimeout < 0) {
          clearMessage(panelId, ENGAGEMENT_MESSAGE_TYPE.TOAST);
          if (linkedContent?.onDismiss) {
            linkedContent.onDismiss();
          }

          return null;
        }

        if (
          !linkedContent &&
          panelId !== ENGAGEMENT_PANEL_IDS.AGENDA_ITEM_REMINDERS &&
          panelId !== ENGAGEMENT_PANEL_IDS.PLANNER_NOTIFICATIONS
        ) {
          return null;
        }

        // Determine the toast content based on the toast content type
        let content = {};
        if (contentType === 'WebUrl') {
          content = {
            href: linkedContent.href,
            name: linkedContent.name
          };
        } else if (
          contentType === 'LinkedPage' ||
          contentType === 'LinkedExternalPage'
        ) {
          content = {
            type: linkedContent.page,
            id: linkedContent.entityId
          };
        } else if (contentType === 'LinkedCallback') {
          content = {
            name: linkedContent.name || linkedContent.renderName?.()
          };
        }

        const key = `${panelId}-${index}`;

        const setToastHeight = e => {
          toastsHeightRef.current[index] =
            (e?.getBoundingClientRect().height || 0) + GAP_BETWEEN_TOASTS;
        };

        const totalHeight = toastsHeightRef.current
          .slice(0, index)
          .reduce((p, c) => p + c, 0);
        const top = topPosition + TOAST_PADDING_TOP + totalHeight; // Using for tablet or desktop view
        const bottom = CHAT_BOT_HEIGHT + totalHeight; // Using for mobile view

        // Check the format of the notifications
        let notificationsData = data;
        if (!notificationsData) {
          notificationsData = [
            {
              icon,
              image,
              body,
              renderBody,
              id: panelId,
              linkedContent: {
                content,
                contentType
              },
              publishedAt
            }
          ];
        }

        // Does this toast support clicking on the settings icon?
        const onSettingsAction = {
          ...(onSettingsClick && {
            onSettingsClick: () => {
              onClick(panelId);
              onSettingsClick();
              toastsHeightRef.current[index] = 0;
            }
          })
        };

        return (
          <div key={key}>
            {panelId === ENGAGEMENT_PANEL_IDS.AGENDA_ITEM_REMINDERS ? (
              <AgendaItemReminderToast
                toastRef={setToastHeight}
                messages={data}
                eventId={eventId}
                dismissTimeout={dismissTimeout}
                bottomPosition={bottom}
                topPosition={top}
                onDismiss={() => {
                  clearMessage(panelId, ENGAGEMENT_MESSAGE_TYPE.TOAST);
                  toastsHeightRef.current[index] = 0;
                  didCalculateHeight.current = false;
                  applyToastsHeightAndTopPosition({});
                  onDismiss();
                }}
              />
            ) : (
              <div
                css={{
                  position: 'fixed',
                  right: 8,
                  padding: 20,
                  ...(isMobileOrTablet ? { bottom } : { top })
                }}
              >
                <NotificationAlert
                  toastRef={setToastHeight}
                  testID={panelId}
                  displayPosition={
                    isMobile
                      ? {
                          position: 'fixed',
                          bottom,
                          right: 15,
                          left: 15
                        }
                      : {
                          position: 'fixed',
                          right: 8,
                          top
                        }
                  }
                  notificationsData={notificationsData}
                  autoHideTimeout={toastTimeout}
                  onDismiss={() => {
                    clearMessage(panelId, ENGAGEMENT_MESSAGE_TYPE.TOAST);
                    toastsHeightRef.current[index] = 0;
                    if (linkedContent?.onDismiss) {
                      linkedContent.onDismiss();
                    }
                    if (onDismiss) {
                      onDismiss();
                    }
                  }}
                  onAlertClick={() => {
                    if (
                      notificationsData[0]?.channelName &&
                      isApptNotificationChannel(
                        notificationsData[0].channelName
                      ) &&
                      !notificationsData[0].linkedContent
                    ) {
                      return;
                    }
                    onClick(panelId);
                    toastsHeightRef.current[index] = 0;
                    if (linkedContent && linkedContent.onClick) {
                      linkedContent.onClick();
                    }
                    if (onAlertClick) {
                      onAlertClick();
                    }
                  }}
                  {...onSettingsAction}
                  linkedContentStyles={linkedContentStyles}
                  eventId={eventId}
                  surveyUrl={surveyUrl}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

EngagementToasts.propTypes = {
  clearMessage: PropTypes.func.isRequired,
  eventId: PropTypes.string,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.array,
      panelId: PropTypes.string.isRequired,
      publishedAt: PropTypes.instanceOf(Date).isRequired,
      message: PropTypes.shape({
        body: PropTypes.string.isRequired,
        contentType: PropTypes.oneOf([
          'WebUrl',
          'LinkedPage',
          'LinkedExternalPage',
          'LinkedCallback'
        ]).isRequired,
        linkedContent: PropTypes.oneOfType([
          PropTypes.shape({
            href: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired
          }),
          PropTypes.shape({
            page: PropTypes.string.isRequired,
            entityId: PropTypes.string.isRequired
          }),
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            onClick: PropTypes.func.isRequired
          })
        ]).isRequired
      })
    })
  ),
  topPosition: PropTypes.number.isRequired,
  surveyUrl: PropTypes.string
};

export default React.memo(EngagementToasts);

export function EngagementToastsConnected() {
  // Grab latest toast messages from context
  const { toastMessages, clearMessage } = useEngagementMessages();
  const { data, loading } = useMeQuery();
  const appFeatures = useAppFeatures();
  const simulateEngagementToasts =
    appFeatures?.simulateEngagementToasts || false;
  const { contentTop } = useBreakpoints();

  // Change the simulateEngagementToasts feature flag to true in appFeatures if you wish to test
  // engagement toasts during local development
  useSimulateEngagementMessages({
    eventId: 'E_001',
    sessionId: 'S_001',
    attendeeId: 'A_001',
    isModerator: false,
    skip: !simulateEngagementToasts
  });

  if (loading || toastMessages.length === 0) {
    return null;
  }

  return (
    <EngagementToasts
      topPosition={contentTop}
      messages={toastMessages}
      clearMessage={clearMessage}
      eventId={data?.me?.event?.id}
      surveyUrl={data?.me?.event?.links?.surveys}
    />
  );
}
